import { ISymbol } from '../components/dialogs/symbolSelectDialog/SymbolSelect'

/**
 * Number of decimal places a symbol's prices carry. One point is the last of
 * them, so this also defines the point size a spread is read in.
 */
export default function getSymbolDecimals(symbolObject?: ISymbol): number {
  if (!symbolObject) return 2

  if (symbolObject.params && symbolObject.params.decimals) {
    return symbolObject.params.decimals
  }

  if (symbolObject.type === 'forex') {
    return symbolObject.name.endsWith('JPY') ? 3 : 5
  }

  return 2
}

/**
 * The tick a price of this many decimal places steps in.
 *
 * toFixed pins the value exactly: 10 ** -5 evaluates to 0.000009999999999999999
 * (and 10 ** -4 to 0.00009999999999999999), and that error would propagate
 * through every price the spread touches.
 */
function tickFromDecimals(decimals: number): number {
  const places = Math.min(Math.max(Math.trunc(decimals), 0), 100)
  return Number((10 ** -places).toFixed(places))
}

/**
 * One point for a symbol — the smallest increment it quotes in.
 *
 * Distinct from getSymbolDecimals: that is how many places a price is DISPLAYED
 * with, which is not a unit. The two coincide for forex but not for indices —
 * U30USD quotes whole numbers while its decimals resolve to 2, so deriving a
 * point from decimals there is 100x too small.
 *
 * params.tickSize is populated for every symbol with observed quotes. The
 * fallback covers the handful the backfill declined and anything added since.
 */
export function getSymbolTickSize(symbolObject?: ISymbol): number {
  const stored = symbolObject?.params?.tickSize
  if (stored && Number.isFinite(stored) && stored > 0) {
    return stored
  }

  return tickFromDecimals(getSymbolDecimals(symbolObject))
}

/**
 * Decimal places needed to render a price at a given tick's precision.
 *
 * Read off the tick's exponential form rather than via -log10, so a tick that
 * is not a power of ten (0.25, 0.5) still renders exactly instead of being
 * rounded to the wrong width. Exponential form also survives ticks small
 * enough that String() would return "1e-7".
 */
export function getTickDecimals(tickSize: number): number {
  if (!Number.isFinite(tickSize) || tickSize <= 0) return 2

  const [mantissa, exponent] = tickSize.toExponential().split('e')
  const mantissaDecimals = (mantissa.split('.')[1] ?? '').length
  return Math.max(0, mantissaDecimals - Number(exponent))
}
